// -----------------------------------------------------------------------------
// Source languages (the learner's native language)
// -----------------------------------------------------------------------------

export const SOURCE_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
] as const;

export type SourceLanguageCode = (typeof SOURCE_LANGUAGES)[number]['code'];

// -----------------------------------------------------------------------------
// Target languages (the language the learner is studying)
// -----------------------------------------------------------------------------

export const TARGET_LANGUAGES = [
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
] as const;

export type TargetLanguageCode = (typeof TARGET_LANGUAGES)[number]['code'];

// -----------------------------------------------------------------------------
// Language pair helpers — prevent same-language pairs (e.g. English → English)
// -----------------------------------------------------------------------------

/**
 * Returns the valid target languages for a given source language,
 * filtering out the same language to prevent e.g. Spanish → Spanish.
 */
export function getValidTargetLanguages(source: SourceLanguageCode) {
  return TARGET_LANGUAGES.filter((t) => t.code !== source);
}

/**
 * Checks whether a source/target pair is valid.
 * A pair is valid when the target exists in TARGET_LANGUAGES and
 * the source and target codes are different.
 */
export function isValidLanguagePair(
  source: SourceLanguageCode,
  target: TargetLanguageCode,
): boolean {
  if (source === (target as string)) return false;
  return TARGET_LANGUAGES.some((t) => t.code === target);
}

// -----------------------------------------------------------------------------
// CEFR proficiency levels
// -----------------------------------------------------------------------------

export const CEFR_LEVELS = [
  {
    code: 'a1',
    name: 'A1 — Beginner',
    description: 'Can understand and use familiar everyday expressions and very basic phrases.',
  },
  {
    code: 'a2',
    name: 'A2 — Elementary',
    description: 'Can communicate in simple, routine tasks on familiar topics.',
  },
  {
    code: 'b1',
    name: 'B1 — Intermediate',
    description: 'Can deal with most situations likely to arise while travelling.',
  },
  {
    code: 'b2',
    name: 'B2 — Upper Intermediate',
    description: 'Can interact with a degree of fluency and spontaneity.',
  },
  {
    code: 'c1',
    name: 'C1 — Advanced',
    description:
      'Can express ideas fluently and spontaneously without much searching for expressions.',
  },
] as const;

export type CefrLevelCode = (typeof CEFR_LEVELS)[number]['code'];

// -----------------------------------------------------------------------------
// Pace tiers — determines workbook size and block count
// -----------------------------------------------------------------------------

export const PACE_TIERS = [
  {
    code: 'light',
    name: 'Light',
    description: 'A gentler workload — perfect for casual learners.',
    pages: 24,
    blockCount: { min: 8, max: 9 },
    conversations: 1,
    vocabGrids: 2,
    otherBlocks: { min: 5, max: 6 },
  },
  {
    code: 'regular',
    name: 'Regular',
    description: 'Full coverage of all exercise types — our most popular.',
    pages: 32,
    blockCount: { min: 11, max: 12 },
    conversations: 2,
    vocabGrids: 3,
    otherBlocks: { min: 6, max: 7 },
  },
  {
    code: 'intensive',
    name: 'Intensive',
    description: 'Maximum practice — for dedicated learners who want rapid progress.',
    pages: 40,
    blockCount: { min: 15, max: 16 },
    conversations: 3,
    vocabGrids: 4,
    otherBlocks: { min: 8, max: 9 },
  },
  {
    code: 'complete',
    name: 'Complete',
    description: 'A comprehensive 100-page workbook with level progression.',
    pages: 100,
    blockCount: { min: 58, max: 62 },
    conversations: 8,
    vocabGrids: 12,
    otherBlocks: { min: 38, max: 42 },
  },
] as const;

export type PaceCode = (typeof PACE_TIERS)[number]['code'];

// -----------------------------------------------------------------------------
// Interest topics — multi-select in the personalization form
// -----------------------------------------------------------------------------

export const TOPICS = [
  { code: 'travel', name: 'Travel', emoji: '✈️' },
  { code: 'work', name: 'Work & Business', emoji: '💼' },
  { code: 'medical', name: 'Medical & Health', emoji: '🏥' },
  { code: 'social', name: 'Social & Dating', emoji: '💬' },
  { code: 'food', name: 'Food & Restaurants', emoji: '🍽️' },
  { code: 'shopping', name: 'Shopping', emoji: '🛍️' },
  { code: 'daily', name: 'Daily Routines', emoji: '🏠' },
  { code: 'culture', name: 'Culture & Entertainment', emoji: '🎭' },
] as const;

export type TopicCode = (typeof TOPICS)[number]['code'];

// -----------------------------------------------------------------------------
// Learning reasons — single-select in the personalization form
// -----------------------------------------------------------------------------

export const LEARNING_REASONS = [
  { code: 'holiday', name: 'Holiday', description: "I'm going on vacation" },
  { code: 'moving', name: 'Moving abroad', description: "I'm relocating to another country" },
  { code: 'partner', name: "Partner's language", description: 'My partner speaks this language' },
  { code: 'work', name: 'Work', description: 'I need it for my job or career' },
  { code: 'general', name: 'General interest', description: 'I just want to learn something new' },
] as const;

export type LearningReasonCode = (typeof LEARNING_REASONS)[number]['code'];

// -----------------------------------------------------------------------------
// Situations — real-life scenarios the learner wants to handle
// -----------------------------------------------------------------------------

export const SITUATIONS = [
  {
    code: 'ordering_food',
    name: 'Ordering food',
    emoji: '🍽️',
    examples: ['restaurants', 'cafés', 'street food', 'dietary needs'],
  },
  {
    code: 'asking_directions',
    name: 'Asking directions',
    emoji: '🗺️',
    examples: ['public transport', 'walking routes', 'finding places'],
  },
  {
    code: 'small_talk',
    name: 'Small talk',
    emoji: '💬',
    examples: ['neighbors', 'colleagues', 'at a party', 'waiting rooms'],
  },
  {
    code: 'appointments',
    name: 'Appointments',
    emoji: '📅',
    examples: ['doctor', 'hairdresser', 'bank', 'government offices'],
  },
  {
    code: 'shopping_errands',
    name: 'Shopping & errands',
    emoji: '🛒',
    examples: ['supermarket', 'pharmacy', 'post office', 'returns'],
  },
  {
    code: 'work_meetings',
    name: 'Work & meetings',
    emoji: '💼',
    examples: ['presentations', 'emails', 'phone calls', 'introductions'],
  },
  {
    code: 'housing',
    name: 'Housing',
    emoji: '🏠',
    examples: ['renting', 'utilities', 'neighbors', 'repairs'],
  },
  {
    code: 'emergencies',
    name: 'Emergencies',
    emoji: '🚨',
    examples: ['police', 'hospital', 'lost items', 'breakdowns'],
  },
] as const;

export type SituationCode = (typeof SITUATIONS)[number]['code'];

// -----------------------------------------------------------------------------
// Learning focus — what aspect of the language the learner wants to prioritize
// -----------------------------------------------------------------------------

export const LEARNING_FOCUS = [
  { code: 'vocabulary', name: 'Vocabulary', description: 'Build a bigger word bank' },
  { code: 'conversation', name: 'Conversation', description: 'Practice real dialogues' },
  { code: 'grammar', name: 'Grammar', description: 'Understand the rules' },
  { code: 'reading', name: 'Reading', description: 'Understand written texts' },
  { code: 'translation', name: 'Translation', description: 'Translate between languages' },
] as const;

export type LearningFocusCode = (typeof LEARNING_FOCUS)[number]['code'];

// -----------------------------------------------------------------------------
// Delivery methods
// -----------------------------------------------------------------------------

export const DELIVERY_METHODS = [
  { code: 'digital', name: 'Digital PDF' },
  { code: 'print', name: 'Printed Workbook' },
  { code: 'both', name: 'Digital + Print' },
] as const;

export type DeliveryMethod = (typeof DELIVERY_METHODS)[number]['code'];

// -----------------------------------------------------------------------------
// Shipping countries — ISO 3166-1 alpha-2 codes (Gelato-supported)
// -----------------------------------------------------------------------------

export const SHIPPING_COUNTRIES = [
  { code: 'AT', name: 'Austria' },
  { code: 'AU', name: 'Australia' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BR', name: 'Brazil' },
  { code: 'CA', name: 'Canada' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DE', name: 'Germany' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'ES', name: 'Spain' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GR', name: 'Greece' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'LV', name: 'Latvia' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'US', name: 'United States' },
] as const;

export type ShippingCountryCode = (typeof SHIPPING_COUNTRIES)[number]['code'];
